<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No audit entries for the selected range."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Start Date" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="End Date" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
      </template>

      <div v-for="group in groupedByDate" :key="group.dateKey">
        <ReportGroupHeader :label="group.dateLabel" />
        <div v-for="(item, idx) in group.items" :key="group.dateKey + '-' + idx">
          <ReportListRow clickable @click="toggleExpand(item, idx, group.dateKey)">
            <template #leading>
              <div class="report-payment-badge report-payment-badge--other">
                <v-icon dark size="18">{{ auditIcon(item.deletedAuditType) }}</v-icon>
              </div>
            </template>
            <template #title>{{ auditLabel(item.deletedAuditType) }} · {{ item.user || '—' }}</template>
            <template #subtitle>{{ item.reason || 'No reason' }}</template>
            <template #trailing>
              <span class="report-time">{{ moment(item.deletedAt).format('HH:mm') }}</span>
            </template>
          </ReportListRow>
          <div v-if="expandedKey === group.dateKey + '-' + idx" class="report-expanded">
            <p v-if="item.description">{{ item.description }}</p>
            <div class="report-expanded__actions">
              <v-btn small color="error" @click.stop="deleteAudit(item)">
                <v-icon left small>mdi-delete</v-icon>
                Delete
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </ReportMobileFrame>

    <template v-else>
   <v-toolbar flat class="toolbar-container mb-2" height='100'>
      <v-row>
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>
        </v-col>
      </v-row>
    </v-toolbar>
    <v-row>
      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
       <v-data-table 
        :headers="headers" :items="data" 
        :loading="isLoading" :items-per-page="-1"
        item-key="deletedAt" class="elevation-1 mt-5 flex-grow-1">

        <template v-slot:[`item.deletedAt`]="{ item }">
          {{ moment(item.deletedAt).format("DD/MM/yyyy HH:mm:ss") }}
        </template>

        <template v-slot:[`item.deletedAuditType`]="{ item }">
          <span v-if="item.deletedAuditType == 0">Order</span>
          <span v-if="item.deletedAuditType == 1">Table</span>
          <span v-if="item.deletedAuditType == 2">Item</span>
        </template>
        
         <template v-slot:[`item.option`]="{ item }">
          <v-btn type="button" color="red" v-on:click="deleteAudit(item)">
            DELETE
          </v-btn>
        </template>
      </v-data-table>
    </v-row>
    </template>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import moment from 'moment';
import { reportService } from '@/services';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'Audit',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    dates: [new Date().toISOString().substr(0, 10)],
    isLoading: false,
    expandedKey: null,
    data: [],
    headers: [
      { text: 'Deleted At', align: 'start', sortable: true, filterable: true, value: 'deletedAt' },
      { text: 'Staff', align: 'start', sortable: true, filterable: true, value: 'user' },
      { text: 'Reason', align: 'start', sortable: true, filterable: true, value: 'reason' },
      { text: 'Audit', align: 'start', sortable: true, filterable: true, value: 'deletedAuditType' },
      { text: 'Description', align: 'start', sortable: true, filterable: true, value: 'description' },
      { text: 'Option', align: 'start', sortable: false, filterable: false, value: 'option' },
    ],
  }),
  async created () {
    if (userService.isStaff()) {
      router.push('/products');
    }
    
    this.load();
  },
 methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      this.data = await reportService.getAuditReport(startDate, endDate);
      this.isLoading = false;
    },
    async deleteAudit(audit) {
      var response = confirm("Are you sure you want to delete this audit?");
      if (!response)
        return;

      var idx = this.data.indexOf(audit);
      await reportService.deleteAudit(audit.id);
      this.data.splice(idx, 1)
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    toggleExpand(item, idx, dateKey) {
      const key = dateKey + '-' + idx;
      this.expandedKey = this.expandedKey === key ? null : key;
    },
    auditLabel(type) {
      switch (type) {
        case 0: return 'Order';
        case 1: return 'Table';
        case 2: return 'Item';
        default: return 'Audit';
      }
    },
    auditIcon(type) {
      switch (type) {
        case 0: return 'mdi-receipt-text-outline';
        case 1: return 'mdi-table-furniture';
        case 2: return 'mdi-package-variant';
        default: return 'mdi-clipboard-search-outline';
      }
    }
  },
  computed: {
    groupedByDate() {
      const sorted = this.data.slice().sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt));
      const groups = new Map();
      for (const item of sorted) {
        const m = this.moment(item.deletedAt);
        const key = m.format('YYYY-MM-DD');
        if (!groups.has(key)) groups.set(key, { dateKey: key, dateLabel: m.format('DD MMM YYYY'), items: [] });
        groups.get(key).items.push(item);
      }
      return Array.from(groups.values());
    }
  }
}
</script>

<style lang="scss" scoped>
  .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

</style>
